import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker';

// 일부 모바일 브라우저는 모듈 방식 워커를 불러오지 못한다.
// Vite가 일반(클래식) 워커로 묶어주는 ?worker 방식을 써서 어떤 폰에서도 열리게 한다.
let workerPort: Worker | null = null;
function ensureWorker(): void {
  if (workerPort) return;
  workerPort = new PdfWorker();
  GlobalWorkerOptions.workerPort = workerPort;
}

/** 캡처 선명도를 위해 페이지를 렌더링할 배율 (데스크톱) */
const BASE_SCALE = 2.5;
/** 페이지 한 변의 최대 픽셀 (너무 큰 캔버스 방지, 데스크톱) */
const MAX_PAGE_PIXELS = 4200;
/** 모바일은 메모리가 부족해 렌더링이 실패할 수 있어 더 가볍게 렌더링한다 */
const MOBILE_BASE_SCALE = 1.6;
const MOBILE_MAX_PAGE_PIXELS = 2200;

/** 모바일(터치) 환경인지 판별 */
function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(navigator.userAgent)) return true;
  return typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;
}

/** 렌더링된 PDF 페이지 한 장 */
export interface RenderedPage {
  /** 0부터 시작하는 페이지 인덱스 */
  index: number;
  /** 원본 해상도 캔버스 (캡처 시 이 캔버스에서 잘라냄) */
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  /** 화면 표시용 이미지(Data URL) */
  dataUrl: string;
}

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** PDF 파일을 읽어 모든 페이지를 고해상도 캔버스 + 이미지로 렌더링한다. */
export async function loadPdfPages(file: File): Promise<RenderedPage[]> {
  const arrayBuffer = await file.arrayBuffer();
  ensureWorker();
  const doc = await getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
  const pages: RenderedPage[] = [];
  const mobile = isMobileDevice();
  const baseScale = mobile ? MOBILE_BASE_SCALE : BASE_SCALE;
  const maxPixels = mobile ? MOBILE_MAX_PAGE_PIXELS : MAX_PAGE_PIXELS;

  for (let i = 1; i <= doc.numPages; i += 1) {
    const page = await doc.getPage(i);
    const base = page.getViewport({ scale: 1 });
    const longEdge = Math.max(base.width, base.height);
    let scale = baseScale;
    if (longEdge * scale > maxPixels) {
      scale = maxPixels / longEdge;
    }

    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);

    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;

    pages.push({
      index: i - 1,
      canvas,
      width: canvas.width,
      height: canvas.height,
      dataUrl: canvas.toDataURL('image/png'),
    });
  }

  return pages;
}

/**
 * 원본 페이지 캔버스에서 지정한 영역을 잘라 이미지(Data URL)로 반환한다.
 * 영역이 너무 작으면 null.
 */
export function cropPageImage(canvas: HTMLCanvasElement, rect: CropRect): string | null {
  const x = Math.max(0, Math.min(canvas.width, Math.round(rect.x)));
  const y = Math.max(0, Math.min(canvas.height, Math.round(rect.y)));
  const w = Math.max(0, Math.min(canvas.width - x, Math.round(rect.width)));
  const h = Math.max(0, Math.min(canvas.height - y, Math.round(rect.height)));
  if (w < 4 || h < 4) return null;

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(canvas, x, y, w, h, 0, 0, w, h);
  return out.toDataURL('image/png');
}